import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import { CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useTrainingBudgets, useTrainingCatalogueMutations } from '../api'
import { Select } from '@/components/ui/select'

/** What may be spent on training, and what is committed against it. */
export function TrainingBudgetsPage() {
  useBreadcrumb()
  const budgets = useTrainingBudgets()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const departments = useLookup<{ id: string; name: string }>('departments')
  const { saveBudget } = useTrainingCatalogueMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; departmentId: string; fiscalYear: string; budgetAmount: string
    currency: string; notes: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Training budgets"
        subtitle="What may be spent, per entity per year, and what is committed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '', departmentId: '',
                fiscalYear: String(new Date().getFullYear()), budgetAmount: '',
                currency: 'AED', notes: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New budget
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Training cost is recorded here, never paid through payroll — settling a provider's
        invoice is Finance's job. A nomination that takes the year over budget is flagged for
        the approver, not refused.
      </p>

      <Card>
        {budgets.isPending && <Skeleton className="m-4 h-20" />}
        {budgets.isError && <ErrorState error={budgets.error} onRetry={() => void budgets.refetch()} />}
        {budgets.isSuccess && budgets.data.length === 0 && (
          <EmptyState
            title="No training budget is set"
            description="Without one, nominations record that nothing was checked — which is not the same as being within budget."
          />
        )}
        {budgets.isSuccess && budgets.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Year</th>
                  <th className="p-3 text-start font-medium">Scope</th>
                  <th className="p-3 text-end font-medium">Budget</th>
                  <th className="p-3 text-end font-medium">Committed</th>
                  <th className="p-3 text-end font-medium">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {budgets.data.map((budget) => (
                  <tr key={budget.id} className="border-b border-slate-100">
                    <td className="p-3">{budget.fiscalYear}</td>
                    <td className="p-3 text-slate-600">
                      {budget.departmentName ?? 'The whole entity'}
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={budget.budgetAmount} currency={budget.currency} />
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={budget.committed} currency={budget.currency} />
                    </td>
                    <td className={`p-3 text-end font-medium ${
                      budget.remaining < 0 ? 'text-amber-700' : ''
                    }`}>
                      <CurrencyValue value={budget.remaining} currency={budget.currency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="New training budget"
        submitLabel="Save budget"
        loading={saveBudget.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveBudget.mutateAsync({
                legalEntityId: draft.legalEntityId,
                departmentId: draft.departmentId || null,
                fiscalYear: Number(draft.fiscalYear),
                budgetAmount: Number(draft.budgetAmount),
                currency: draft.currency,
                notes: draft.notes || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That budget could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="budget-entity">Legal entity</Label>
              <Select
                id="budget-entity"
                className="w-full"
                value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                <option value="">Choose an entity…</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="budget-department">Department</Label>
              <Select
                id="budget-department"
                className="w-full"
                value={draft.departmentId}
                onChange={(event) => setDraft({ ...draft, departmentId: event.target.value })}
              >
                <option value="">The whole entity</option>
                {departments.data?.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="budget-year">Fiscal year</Label>
              <Input
                id="budget-year" type="number" value={draft.fiscalYear}
                onChange={(event) => setDraft({ ...draft, fiscalYear: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="budget-amount">Amount</Label>
              <Input
                id="budget-amount" type="number" value={draft.budgetAmount}
                onChange={(event) => setDraft({ ...draft, budgetAmount: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="budget-currency">Currency</Label>
              <Input
                id="budget-currency" maxLength={3} value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="budget-notes">Notes</Label>
              <Input
                id="budget-notes" value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
